import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_PRODUCT = {
  name: '',
  description: '',
  price: 0,
  category: 'Combos',
  image_url: '',
  available: true,
};

const AdminProducts = () => {
  const { authAxios } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PRODUCT);
  const [uploading, setUploading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const fetchAll = async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
      ]);
      setProducts(pRes.data);
      setCategories(cRes.data);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...EMPTY_PRODUCT, category: categories[0]?.name || 'Combos' });
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setFormData({ ...product });
    setDialogOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await authAxios.post('/admin/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${BACKEND_URL}${response.data.url}`;
      setFormData({ ...formData, image_url: url });
      toast.success('Imagem enviada!');
    } catch {
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      toast.error('Adicione uma imagem do produto');
      return;
    }
    try {
      if (editing) {
        await authAxios.put(`/admin/products/${editing.id}`, formData);
        toast.success('Produto atualizado!');
      } else {
        await authAxios.post('/admin/products', formData);
        toast.success('Produto criado!');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este produto?')) return;
    try {
      await authAxios.delete(`/admin/products/${id}`);
      toast.success('Produto deletado!');
      fetchAll();
    } catch {
      toast.error('Erro ao deletar produto');
    }
  };

  const toggleAvailable = async (product) => {
    try {
      await authAxios.put(`/admin/products/${product.id}`, { available: !product.available });
      fetchAll();
    } catch {
      toast.error('Erro ao atualizar');
    }
  };

  const filteredProducts = categoryFilter === 'all'
    ? products
    : products.filter((p) => p.category === categoryFilter);

  if (loading) return <div className="text-center py-10 text-[#5C4247]">Carregando produtos...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          className="text-2xl font-bold text-[#291014]"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="products-section-title"
        >
          Produtos ({filteredProducts.length})
        </h2>
        <div className="flex gap-2 flex-wrap">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="products-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas categorias</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={openCreate}
            className="bg-[#7F1D1D] hover:bg-[#991B1B] text-white rounded-full"
            data-testid="add-product-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Produto
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {filteredProducts.map((p) => (
          <div
            key={p.id}
            className={`bg-white rounded-2xl border border-[#F3E8E3] overflow-hidden ${
              !p.available ? 'opacity-60' : ''
            }`}
            data-testid={`admin-product-${p.id}`}
          >
            <div className="aspect-video bg-gray-100">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-bold text-[#291014]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {p.name}
                </h3>
                <Switch
                  checked={p.available}
                  onCheckedChange={() => toggleAvailable(p)}
                  data-testid={`product-available-switch-${p.id}`}
                />
              </div>
              <p className="text-xs text-[#5C4247] mb-1">{p.category}</p>
              <p className="text-xs text-[#5C4247] mb-2 line-clamp-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {p.description}
              </p>
              <div className="flex items-center justify-between mt-3">
                <span
                  className="font-bold text-[#7F1D1D]"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                  data-testid={`admin-product-price-${p.id}`}
                >
                  R$ {p.price.toFixed(2)}
                </span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(p)}
                    className="h-8 px-2"
                    data-testid={`edit-product-${p.id}`}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(p.id)}
                    className="h-8 px-2 text-red-600 hover:bg-red-50"
                    data-testid={`delete-product-${p.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="product-dialog-title">
              {editing ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados abaixo para {editing ? 'atualizar' : 'cadastrar'} o produto no cardápio.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Foto do Produto *</Label>
              <div className="mt-2">
                {formData.image_url ? (
                  <div className="relative mb-2">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  </div>
                ) : (
                  <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="upload-image-input"
                  />
                  <div className="flex items-center justify-center gap-2 border-2 border-dashed border-[#7F1D1D] text-[#7F1D1D] rounded-lg py-3 hover:bg-[#7F1D1D]/5 transition-colors">
                    <Upload className="h-4 w-4" />
                    <span>{uploading ? 'Enviando...' : 'Enviar imagem'}</span>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <Label>Nome *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label>Descrição *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                data-testid="product-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Preço (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <Label>Categoria *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between bg-[#FAF8F5] rounded-lg p-3">
              <Label htmlFor="available">Produto disponível para venda</Label>
              <Switch
                id="available"
                checked={formData.available}
                onCheckedChange={(v) => setFormData({ ...formData, available: v })}
                data-testid="product-available-toggle"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#7F1D1D] hover:bg-[#991B1B] text-white rounded-full"
              data-testid="save-product-button"
            >
              {editing ? 'Salvar Alterações' : 'Criar Produto'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
